import { Fragment, useEffect, useState, useCallback } from 'react'
import { batchesApi, type Batch } from '../api/batches'
import { BatchChecksPanel } from './BatchesPage'
import { EXPORT_STATUS_LABELS, exportsApi, type ExportRequest } from '../api/exports'
import { Button, Callout, Card, CardHeader, DataTable, Dialog, EmptyState, FormGrid, LoadingState, PageHeader, Page, StatusPill, Tabs, TextField, type PillTone } from '../components/ui'

/**
 * TASK-0026 / ADR-007: lists lotti submitted for approval (InAttesaApprovazione AND already
 * submitted — see Batch.cs's remarks on why SubmittedAt, not a 4th BatchStatus value, tells "still
 * being assembled" apart from "waiting for a Manager's decision") with Approve/Reject actions.
 *
 * This SPA does no client-side route gating (same pattern already established elsewhere in this
 * codebase — see ImplementationStatusPage.tsx): the real gate is the backend's PolicyNames.Manager
 * policy on GET/POST /api/batches/*. A caller who is BackOffice-tier but not Manager-tier can still
 * load the list (BatchesController.List allows BackOffice OR Manager) but gets 403 from
 * approve/reject; anyone with neither policy gets 403 from the list itself. Either way this page
 * must not crash — it shows a plain error message, same as ImplementationStatusPage.tsx's `catch`.
 */
function BatchApprovalsTab() {
  const [items, setItems] = useState<Batch[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionError, setActionError] = useState('')
  // F-078 (REQ-008-003/REQ-009-001): il lotto di cui si stanno guardando i controlli prima di decidere.
  const [checksFor, setChecksFor] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const result = await batchesApi.list({ status: 'InAttesaApprovazione', pageSize: 100 })
      setItems(result.items.filter(b => !!b.submittedAt))
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function handleApprove(id: number) {
    setActionError('')
    setBusyId(id)
    try {
      await batchesApi.approve(id)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Errore durante l’approvazione')
    } finally {
      setBusyId(null)
    }
  }

  function openReject(id: number) {
    setRejectingId(id)
    setRejectReason('')
    setActionError('')
  }

  async function submitReject() {
    if (!rejectingId) return
    if (!rejectReason.trim()) {
      setActionError('La motivazione del rifiuto è obbligatoria.')
      return
    }
    setBusyId(rejectingId)
    try {
      await batchesApi.reject(rejectingId, rejectReason.trim())
      setRejectingId(null)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Errore durante il rifiuto')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="md-stack">
      <h2 className="md-typescale-title-medium m-0">Approvazioni lotti</h2>

      {actionError && !rejectingId && <Callout tone="error">{actionError}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : loadError ? (
          <Callout tone="error">
            Non è stato possibile caricare le approvazioni. {loadError}
          </Callout>
        ) : items.length === 0 ? (
          <EmptyState icon="task_alt" title="Nessun lotto in attesa di approvazione" />
        ) : (
          <DataTable variant="plain" aria-label="Lotti in attesa di approvazione">
            <thead>
              <tr>
                <th>Codice lotto</th>
                <th>Operatore</th>
                <th>Sottomesso il</th>
                <th>Motivazione creazione</th>
                <th className="md-num">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => (
                <Fragment key={b.id}>
                <tr>
                  <td className="md-typescale-emphasized-body-medium font-monospace">{b.batchCode}</td>
                  <td>{b.operatorId ?? '—'}</td>
                  <td>{b.submittedAt ? new Date(b.submittedAt).toLocaleString('it-IT') : '—'}</td>
                  <td>{b.reason ?? '—'}</td>
                  <td className="md-num">
                    <div className="md-cluster justify-content-end">
                      <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        onClick={() => setChecksFor(checksFor === b.id ? null : b.id)}
                      >
                        {checksFor === b.id ? 'Nascondi controlli' : 'Controlli'}
                      </Button>
                      <Button
                        type="button"
                        variant="tonal"
                        size="sm"
                        disabled={busyId === b.id}
                        onClick={() => void handleApprove(b.id)}
                      >
                        Approva
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        disabled={busyId === b.id}
                        onClick={() => openReject(b.id)}
                      >
                        Rifiuta
                      </Button>
                    </div>
                  </td>
                </tr>
                {checksFor === b.id && (
                  <tr>
                    <td colSpan={5}>
                      <BatchChecksPanel batchId={b.id} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog
        open={rejectingId !== null}
        onClose={() => setRejectingId(null)}
        title="Rifiuta lotto"
        as="form"
        onSubmit={e => { e.preventDefault(); void submitReject() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setRejectingId(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              tone="error"
              disabled={busyId === rejectingId}
            >
              Conferma rifiuto
            </Button>
          </>
        }
      >
        {actionError && <Callout tone="error">{actionError}</Callout>}
        <FormGrid columns={1}>
          <TextField
            label="Motivazione (obbligatoria)"
            multiline
            rows={3}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
          />
        </FormGrid>
      </Dialog>
    </div>
  )
}

/* ── F-117 (REQ-266-001): approvazione degli export ─────────────────────────────────────────── */

const EXPORT_DATASET_LABELS: Record<string, string> = {
  contracts: 'Pratiche',
  'finance:expenses': 'Spese',
  'finance:invoices': 'Fatture',
  'finance:suppliers': 'Fornitori',
  'finance:accounts': 'Conti',
  'finance:categories': 'Categorie di spesa',
  'finance:cost-centres': 'Centri di costo',
  'finance:partner-movements': 'Movimenti dei soci',
  'finance:movements': 'Movimenti bancari',
  'finance:reconciliations': 'Riconciliazioni',
  'finance:scheduled-payments': 'Scadenze di pagamento',
  'finance:budgets': 'Budget di costo',
}

const EXPORT_STATUS_TONE: Record<ExportRequest['status'], PillTone> = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'error',
  Used: 'neutral',
  Expired: 'neutral',
}

function describeFilters(filters: Record<string, string>): string {
  const entries = Object.entries(filters)
  return entries.length === 0 ? 'nessun filtro' : entries.map(([k, v]) => `${k}: ${v}`).join(', ')
}

function ExportApprovalsTab() {
  const [mine, setMine] = useState<ExportRequest[]>([])
  // null = non sei amministratore (403): la sezione "in attesa" non si mostra.
  const [pending, setPending] = useState<ExportRequest[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [deciding, setDeciding] = useState<{ id: number; approve: boolean } | null>(null)
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setMine((await exportsApi.mine()).items)
      try {
        setPending((await exportsApi.pending()).items)
      } catch {
        setPending(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function decide() {
    if (!deciding) return
    setBusyId(deciding.id)
    setError('')
    try {
      if (deciding.approve) await exportsApi.approve(deciding.id, note.trim() || undefined)
      else await exportsApi.reject(deciding.id, note.trim() || undefined)
      setDeciding(null)
      setNote('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante la decisione')
    } finally {
      setBusyId(null)
    }
  }

  async function download(r: ExportRequest) {
    setBusyId(r.id)
    setError('')
    try {
      await exportsApi.download(r)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il download')
      await load()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="md-stack">
      <h2 className="md-typescale-title-medium m-0">Approvazioni export</h2>
      {error && <Callout tone="error">{error}</Callout>}
      {loading && <LoadingState />}

      {pending !== null && (
        <Card variant="outlined" padding="none">
          <CardHeader title="In attesa di decisione" />
          {pending.length === 0 ? (
            <EmptyState compact icon="inbox" title="Nessuna richiesta di export in attesa." />
          ) : (
            <DataTable variant="plain" aria-label="Richieste di export in attesa di decisione">
              <thead>
                <tr><th>Dataset</th><th>Filtri</th><th>Richiedente</th><th>Motivo</th><th>Richiesta il</th><th className="md-num">Azioni</th></tr>
              </thead>
              <tbody>
                {pending.map(r => (
                  <tr key={r.id}>
                    <td>{EXPORT_DATASET_LABELS[r.dataset] ?? r.dataset}</td>
                    <td className="md-typescale-body-small">{describeFilters(r.filters)}</td>
                    <td>Utente #{r.requestedBy}</td>
                    <td>{r.reason}</td>
                    <td>{new Date(r.createdAt).toLocaleString('it-IT')}</td>
                    <td className="md-num">
                      <div className="md-cluster justify-content-end">
                        <Button type="button" variant="tonal" size="sm" disabled={busyId === r.id}
                          onClick={() => { setDeciding({ id: r.id, approve: true }); setNote('') }}>Approva</Button>
                        <Button type="button" variant="outlined" size="sm" disabled={busyId === r.id}
                          onClick={() => { setDeciding({ id: r.id, approve: false }); setNote('') }}>Rifiuta</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </Card>
      )}

      <Card variant="outlined" padding="none">
        <CardHeader title="Le mie richieste" />
        {mine.length === 0 ? (
          <EmptyState compact icon="inbox" title="Non hai richieste di export." />
        ) : (
          <DataTable variant="plain" aria-label="Le mie richieste di export">
            <thead>
              <tr><th>Dataset</th><th>Filtri</th><th>Stato</th><th>Nota</th><th>Valida fino al</th><th className="md-num">Azioni</th></tr>
            </thead>
            <tbody>
              {mine.map(r => (
                <tr key={r.id}>
                  <td>{EXPORT_DATASET_LABELS[r.dataset] ?? r.dataset}</td>
                  <td className="md-typescale-body-small">{describeFilters(r.filters)}</td>
                  <td><StatusPill tone={EXPORT_STATUS_TONE[r.status]}>{EXPORT_STATUS_LABELS[r.status]}</StatusPill></td>
                  <td>{r.decisionNote ?? '—'}</td>
                  <td>{r.expiresAt ? new Date(r.expiresAt).toLocaleString('it-IT') : '—'}</td>
                  <td className="md-num">
                    {r.status === 'Approved' && (
                      <Button type="button" variant="tonal" size="sm" disabled={busyId === r.id}
                        onClick={() => void download(r)}>Scarica</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        title={deciding?.approve ? 'Approva export' : 'Rifiuta export'}
        as="form"
        onSubmit={e => { e.preventDefault(); void decide() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setDeciding(null)}>Annulla</Button>
            <Button type="submit" variant="text" tone={deciding?.approve ? 'primary' : 'error'}
              disabled={deciding !== null && busyId === deciding.id}>
              {deciding?.approve ? 'Conferma approvazione' : 'Conferma rifiuto'}
            </Button>
          </>
        }
      >
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid columns={1}>
          <TextField label="Nota (facoltativa)" multiline rows={3} maxLength={500} value={note} onChange={e => setNote(e.target.value)} />
        </FormGrid>
      </Dialog>
    </div>
  )
}

/** Approvazioni: lotti (ADR-007) ed export (REQ-266-001, F-117). */
export function ApprovalsPage() {
  const [tab, setTab] = useState<'batches' | 'exports'>('batches')
  return (
    <Page>
      <PageHeader
        title="Approvazioni"
        icon="approval"
        tabs={
          <Tabs
            aria-label="Sezioni approvazioni"
            items={[{ id: 'batches', label: 'Lotti' }, { id: 'exports', label: 'Export' }]}
            value={tab}
            onChange={id => setTab(id === 'exports' ? 'exports' : 'batches')}
          />
        }
      />
      {tab === 'batches' ? <BatchApprovalsTab /> : <ExportApprovalsTab />}
    </Page>
  )
}
